(() => {
  class DDCardPurchaseState extends HTMLElement {
    connectedCallback() {
      this.addedTimer = undefined;
      this.pending = false;
      this.pdpFeedback = this.hasAttribute('data-dd-card-pdp-feedback');
      this.controlWasDisabled = false;
      this.pendingVariantId = '';
      this.controlButton = this.querySelector('[data-dd-card-purchase-button], .dd-card-quick-add__button');
      this.cartErrorEventName = window.StandardEvents?.CartErrorEvent?.eventName || 'cart:error';
      this.addEventListener('dd:card-add-state', this.onCardAddState);
      document.addEventListener('submit', this.onDocumentSubmit, true);
      document.addEventListener(this.cartErrorEventName, this.onNativeCartError);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('dd:card-add-state', this.onCardAddState);
      document.removeEventListener('submit', this.onDocumentSubmit, true);
      document.removeEventListener(this.cartErrorEventName, this.onNativeCartError);
      this.unsubscribeUpdate?.();
      this.unsubscribeError?.();
      window.clearTimeout(this.addedTimer);
    }

    onDocumentSubmit = (event) => {
      const owner = event.target.closest('[data-dd-card-purchase-owner], [data-dd-card-quick-add-owner]');
      const modalOwner = owner?.dataset.ddCardPurchaseOwner || owner?.dataset.ddCardQuickAddOwner;
      if (!this.contains(event.target) && modalOwner !== this.id) return;
      if (this.pending || (this.pdpFeedback && this.classList.contains('is-added'))) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (this.pdpFeedback) {
        const submitButton = event.submitter || event.target.querySelector('[type="submit"]');
        if (submitButton?.disabled || submitButton?.getAttribute('aria-disabled') === 'true') return;
        this.controlWasDisabled = Boolean(this.controlButton?.disabled);
        window.clearTimeout(this.addedTimer);
        this.announce('');
      }
      this.pending = true;
      this.pendingVariantId = String(new FormData(event.target).get('id') || this.dataset.variantId);
      this.classList.add('is-adding');
      this.classList.remove('is-added');
      this.setError('');
      this.controlButton?.setAttribute('aria-busy', 'true');
      this.syncControlDisabled();
    };

    onCardAddState = (event) => {
      if (!this.pdpFeedback || !this.pending || !this.contains(event.target)) return;
      if (String(event.detail?.productVariantId) !== this.pendingVariantId) return;
      if (event.detail.state === 'accepted') {
        this.showAdded();
      } else if (event.detail.state === 'complete') {
        this.pending = false;
        this.syncControlDisabled();
      } else if (event.detail.state === 'error') {
        this.setError(event.detail.message);
        this.clearPendingState();
      } else if (event.detail.state === 'reset') {
        this.announce('');
        this.clearPendingState();
      }
    };

    onCartUpdate = (event) => {
      if (this.pdpFeedback) return;
      if (!this.pending || String(event?.productVariantId) !== this.pendingVariantId) return;
      this.pending = false;
      this.showAdded();
    };

    showAdded() {
      this.classList.remove('is-adding');
      this.classList.add('is-added');
      this.controlButton?.removeAttribute('aria-busy');
      this.announce(
        this.controlButton?.querySelector(
          '[data-dd-card-purchase-added-label], .dd-card-quick-add__face--added .dd-card-quick-add__label'
        )?.textContent
      );
      window.clearTimeout(this.addedTimer);
      this.addedTimer = window.setTimeout(() => {
        this.classList.remove('is-added');
        this.syncControlDisabled();
      }, 1600);
      this.syncControlDisabled();
    }

    syncControlDisabled() {
      if (!this.pdpFeedback || !this.controlButton) return;
      this.controlButton.disabled = this.controlWasDisabled || this.pending || this.classList.contains('is-added');
    }

    onCartError = (event) => {
      if (this.pdpFeedback) return;
      if (!this.pending || String(event?.productVariantId) !== this.pendingVariantId) return;
      this.setError(event?.errors || event?.message);
      this.clearPendingState();
    };

    onNativeCartError = (event) => {
      if (!this.pending) return;
      const owner = event.target?.closest?.('[data-dd-card-purchase-owner], [data-dd-card-quick-add-owner]');
      const modalOwner = owner?.dataset.ddCardPurchaseOwner || owner?.dataset.ddCardQuickAddOwner;
      if (!this.contains(event.target) && modalOwner !== this.id) return;
      this.setError(event?.error || event?.message || event?.detail?.error || event?.detail?.message);
      this.clearPendingState();
    };

    setError(message) {
      const error = String(message || '').trim();
      const wrapper = this.querySelector('[data-dd-card-purchase-error], .dd-card-quick-add__error');
      const output = wrapper?.querySelector('.product-form__error-message');
      if (output && error) output.textContent = error;
      wrapper?.toggleAttribute('hidden', !error);
      if (error) this.announce(error);
    }

    announce(message) {
      const status = this.querySelector('[data-dd-card-purchase-status], .dd-card-quick-add__status');
      if (status) status.textContent = String(message || '').trim();
    }

    clearPendingState() {
      window.clearTimeout(this.addedTimer);
      this.pending = false;
      this.classList.remove('is-adding');
      this.classList.remove('is-added');
      this.controlButton?.removeAttribute('aria-busy');
      this.syncControlDisabled();
    }
  }

  if (!customElements.get('dd-card-quick-add')) {
    customElements.define('dd-card-quick-add', class DDCardQuickAdd extends DDCardPurchaseState {});
  }

  if (!customElements.get('dd-card-purchase-state')) {
    customElements.define('dd-card-purchase-state', class DDProductCardPurchaseState extends DDCardPurchaseState {});
  }
})();
